import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, asArray, formatApiError } from "@/lib/api";
import { pesoOr } from "@/lib/money";
import { Plus, Pencil, Trash2, RefreshCw, Save, X } from "lucide-react";

// Contract charges: the recurring fee a client pays whether or not anybody
// logged an hour. A billing run picks these up by CADENCE, not by anybody
// remembering, which is why the cadence and the window are the two columns
// that matter on this screen.
//
// ⚠️ THE AMOUNT FIELD TAKES PESOS AND IS SENT AS TYPED. `money.to_minor_required`
// on the server is the only converter, and it refuses anything it cannot price
// rather than guessing. The browser does not multiply by 100 and does not round:
// a price this bundle computed would be a second rounding rule on the same
// figure. Amounts come BACK as integer centavos (`amount_minor`) and are
// rendered through the one formatter.

const CADENCES = [
  ["monthly", "Monthly"],
  ["quarterly", "Quarterly, every 3 months from the start month"],
  ["annual", "Annually, every 12 months from the start month"],
];

const BLANK = {
  contract_id: "", description: "", amount: "",
  cadence: "monthly", starts_on: "", ends_on: "", active: true,
};

// What the server calls a field, and what a person calls it. `changed` comes
// back keyed by the STORED field, so an honest toast built straight from it
// says "Updated amount_minor" -- accurate, and not the name of anything on
// this form. The money one is the whole reason this map exists.
const FIELD_LABEL = {
  amount_minor: "amount",
  description: "description",
  cadence: "cadence",
  starts_on: "start date",
  ends_on: "end date",
  active: "active flag",
};

function fieldLabel(key) {
  return FIELD_LABEL[key] || String(key).replaceAll("_", " ");
}

function cadenceLabel(key) {
  const row = CADENCES.find((c) => c[0] === key);
  return row ? row[1] : String(key || "not recorded");
}

// ⚠️ A CADENCE THE SERVER CANNOT READ IS A CHARGE NO RUN EVALUATES AT ALL --
// it lands in a run's `unevaluated` list, neither billed nor deliberately left
// off. Rendering the stored word on its own is honest and says nothing about
// that, so the row is marked. A charge written by hand can carry one; the
// create route cannot produce one.
function cadenceKnown(key) {
  return CADENCES.some((c) => c[0] === key);
}

// Two contracts in this fleet's seeds routinely share a client AND a title, so
// a picker built from the title alone offers the same words several times and
// the operator cannot tell which one they chose.
function contractOption(c, clientName) {
  return `${clientName(c.client_id)} · ${c.title || "Untitled contract"} · ${c.id}`;
}

export default function ContractCharges() {
  const [charges, setCharges] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState(null);   // charge id being edited
  // ⚠️ THE ROW AS IT STANDS ON THE SERVER, kept beside the form so a save can
  // compare what is being asked for against what is already there. Without it
  // this screen cannot tell "cleared the end date" from "there never was one",
  // and those two need opposite answers.
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterContract, setFilterContract] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const qs = filterContract ? `?contract_id=${encodeURIComponent(filterContract)}` : "";
    api.get(`/contract-charges${qs}`)
      .then((r) => { setCharges(asArray(r.data)); setErr(""); })
      .catch((e) => { setCharges(null); setErr(formatApiError(e)); })
      .finally(() => setLoading(false));
  }, [filterContract]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/contracts").then((r) => setContracts(asArray(r.data))).catch(() => setContracts([]));
    api.get("/clients").then((r) => setClients(asArray(r.data))).catch(() => setClients([]));
  }, []);

  const contractById = useMemo(() => {
    const m = {};
    contracts.forEach((c) => { m[c.id] = c; });
    return m;
  }, [contracts]);
  const clientName = useCallback((id) => clients.find((c) => c.id === id)?.name || id || "No client recorded", [clients]);

  const startCreate = () => { setEditing(null); setEditingRow(null); setForm(BLANK); setBanner(null); };
  const startEdit = (c) => {
    setEditing(c.id);
    setEditingRow(c);
    setBanner(null);
    setForm({
      contract_id: c.contract_id || "",
      description: c.description || "",
      // ⚠️ PRE-FILLED FROM THE INTEGER, as a plain decimal string -- not from a
      // float and not through the display formatter, whose output carries a
      // currency symbol and thousands separators the server would refuse.
      amount: c.amount_minor === null || c.amount_minor === undefined
        ? "" : (c.amount_minor / 100).toFixed(2),
      cadence: c.cadence || "monthly",
      starts_on: c.starts_on || "",
      ends_on: c.ends_on || "",
      active: c.active !== false,
    });
  };
  const cancel = () => { setEditing(null); setEditingRow(null); setForm(BLANK); };

  const save = async () => {
    setSaving(true); setBanner(null);
    try {
      if (editing) {
        // ⚠️⚠️ **CLEARING AN END DATE IS REFUSED HERE, LOUDLY, BEFORE ANYTHING
        // IS SENT.** `ChargeUpdate.ends_on = None` means "NOT SUPPLIED" on the
        // server, so a blank cannot clear a stored end date -- and this screen
        // used to drop the blank and then fire a success toast anyway. The
        // operator was told the save worked, the row was unchanged, and from
        // the month after that stored date the charge silently stopped
        // appearing on every run with nothing anywhere saying why.
        //
        // That is the exact defect this whole sub-project exists to prevent --
        // a bill quietly short, footing perfectly, found by a client who is
        // undercharged and says nothing -- arriving through the screen built
        // to prevent it. A sentence in small print at the bottom of the form
        // is not a control; this is.
        //
        // CHANGING an end date still works, so the field stays editable: only
        // the impossible operation is refused, and it is refused by name.
        if (editingRow?.ends_on && !form.ends_on.trim()) {
          setBanner(
            "This charge ends on " + editingRow.ends_on + " and that date CANNOT be "
            + "cleared here: the update route reads a blank as \"leave it alone\", so "
            + "saving would report success and change nothing. Nothing has been sent. "
            + "To keep billing this client past " + editingRow.ends_on + ", add a NEW "
            + "charge starting the month after it ends. That is also the honest record "
            + "of what happened: the old agreement ended and a new one began.");
          return;                       // `finally` below resets `saving`
        }
        const body = {
          description: form.description.trim(),
          amount: form.amount.trim(),
          cadence: form.cadence,
          starts_on: form.starts_on.trim(),
          active: form.active,
        };
        if (form.ends_on.trim()) body.ends_on = form.ends_on.trim();
        const { data } = await api.patch(`/contract-charges/${editing}`, body);
        // ⚠️ **THE TOAST REPORTS THE OUTCOME, NOT THE ATTEMPT.** It used to fire
        // unconditionally, so a request that moved nothing still said
        // "updated". `update_charge` already returns `changed` -- the fields
        // that actually MOVED, with every no-op dropped -- and until now
        // nothing rendered it, which is this sub-project's recurring shape all
        // over again. A 200 is not a change.
        const moved = Object.keys(data?.changed || {});
        if (moved.length) {
          toast.success("Updated " + moved.map(fieldLabel).join(", "));
        } else {
          setBanner("Nothing changed: every value submitted already matched the "
                    + "stored charge, so the server wrote nothing. This is not an "
                    + "error, and it is not a save either.");
        }
      } else {
        await api.post("/contract-charges", {
          contract_id: form.contract_id,
          description: form.description.trim(),
          amount: form.amount.trim(),
          cadence: form.cadence,
          starts_on: form.starts_on.trim(),
          ends_on: form.ends_on.trim() || null,
          active: form.active,
        });
        toast.success("Contract charge created");
      }
      setForm(BLANK); setEditing(null); load();
    } catch (e) {
      setBanner(formatApiError(e));
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    const ok = window.confirm(
      `Delete "${c.description}"?\n\nThis is permanent. It is refused while any billing run still holds a line for this charge, and in that case the run has to be released first, or the charge deactivated or end-dated instead.`);
    if (!ok) return;
    setBanner(null);
    try {
      await api.delete(`/contract-charges/${c.id}`);
      toast.success("Contract charge deleted");
      load();
    } catch (e) {
      setBanner(formatApiError(e));
    }
  };

  const rows = asArray(charges);

  return (
    <div className="space-y-4" data-testid="contract-charges-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Billing · Preparation</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Contract Charges</h1>
          <div className="text-xs text-zinc-500 mt-1">
            Recurring fees a billing run adds to a client's month by cadence, whether or not anybody logged an hour.
          </div>
        </div>
        <button onClick={load} data-testid="contract-charges-refresh"
          className="border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900 flex items-center gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {banner && (
        <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm whitespace-pre-wrap" data-testid="contract-charges-banner">
          {banner}
        </div>
      )}

      {/* Create / edit */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-3" data-testid="contract-charge-form">
        <div className="text-sm font-medium text-zinc-200 flex items-center gap-2">
          {editing ? <Pencil size={15} className="text-zinc-500" /> : <Plus size={15} className="text-zinc-500" />}
          {editing ? "Edit charge" : "Add a charge"}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <label className="text-xs text-zinc-500 space-y-1">
            <div>Contract</div>
            <select value={form.contract_id} disabled={!!editing}
              onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
              data-testid="charge-form-contract"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 disabled:opacity-50">
              <option value="">Choose a contract</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>{contractOption(c, clientName)}</option>
              ))}
            </select>
            {editing && <div className="text-[10px] text-zinc-600">A charge cannot move to another contract, because that moves it to another client's bill. Create it there instead.</div>}
          </label>
          <label className="text-xs text-zinc-500 space-y-1">
            <div>Description</div>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Managed endpoints retainer" data-testid="charge-form-description"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200" />
          </label>
          <label className="text-xs text-zinc-500 space-y-1">
            <div>Amount per occurrence (pesos)</div>
            <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="45000.00" inputMode="decimal" data-testid="charge-form-amount"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 font-mono" />
          </label>
          <label className="text-xs text-zinc-500 space-y-1">
            <div>Cadence</div>
            <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })}
              data-testid="charge-form-cadence"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200">
              {CADENCES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </label>
          <label className="text-xs text-zinc-500 space-y-1">
            <div>Starts on</div>
            <input type="date" value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })}
              data-testid="charge-form-starts"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 font-mono" />
          </label>
          <label className="text-xs text-zinc-500 space-y-1">
            <div>Ends on (leave blank for open-ended)</div>
            <input type="date" value={form.ends_on} onChange={(e) => setForm({ ...form, ends_on: e.target.value })}
              data-testid="charge-form-ends"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 font-mono" />
            {/* Said BEFORE the operator tries it, in the same shape the
                contract picker above uses. The refusal in `save` is the
                control; this is so nobody meets the control by surprise. */}
            {editingRow?.ends_on && (
              <div className="text-[10px] text-amber-300/80" data-testid="charge-form-ends-note">
                This date can be MOVED but not cleared. To bill past it, add a new charge starting the month after it ends.
              </div>
            )}
          </label>
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-400">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
            data-testid="charge-form-active" className="accent-blue-600" />
          Active. An inactive charge is never billed again, and stays on every run that already billed it.
        </label>
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={saving || !form.description.trim() || !form.starts_on || (!editing && !form.contract_id)}
            data-testid="charge-form-save"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1.5">
            <Save size={14} /> {saving ? "Saving…" : editing ? "Save changes" : "Add charge"}
          </button>
          {editing && (
            <button onClick={cancel} data-testid="charge-form-cancel"
              className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900 flex items-center gap-1.5">
              <X size={14} /> Cancel
            </button>
          )}
          {!editing && (form.description || form.amount) && (
            <button onClick={startCreate} data-testid="charge-form-clear"
              className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-400 hover:bg-zinc-900">Clear</button>
          )}
        </div>
        <div className="text-[11px] text-zinc-600 leading-relaxed">
          A charge lands whole in its due month: a contract that ends part way through a month still bills that whole month, because charges are never prorated.
          Once a charge has an end date this form cannot clear it again, so un-ending a charge means adding a new one starting the month after the old one ended, which is also the honest record of what happened.
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-3 items-end" data-testid="contract-charges-filters">
        <label className="text-xs text-zinc-500 space-y-1">
          <div>Contract</div>
          <select value={filterContract} onChange={(e) => setFilterContract(e.target.value)}
            data-testid="contract-charges-filter"
            className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 min-w-[20rem]">
            <option value="">Every contract</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>{contractOption(c, clientName)}</option>
            ))}
          </select>
        </label>
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm" data-testid="contract-charges-error">{err}</div>}
      {loading && !err && <div className="text-zinc-600 text-sm">Loading…</div>}

      {!loading && !err && !rows.length && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-8 text-sm text-zinc-400" data-testid="contract-charges-empty">
          {filterContract
            ? "This contract carries no recurring charges. A run for its client will bill logged time only."
            : "No recurring charge has been set up yet. Every billing run will bill logged time only until one is added above."}
        </div>
      )}

      {!!rows.length && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden" data-testid="contract-charges-table">
          <table className="w-full text-sm hidden lg:table">
            <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Contract</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-left px-4 py-3">Cadence</th>
                <th className="text-left px-4 py-3">Window</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">State</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`charge-row-${c.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                  <td className="px-4 py-2 text-zinc-300">{clientName(c.client_id)}</td>
                  <td className="px-4 py-2 text-zinc-400 text-xs">{contractById[c.contract_id]?.title || c.contract_id || "No contract recorded"}</td>
                  <td className="px-4 py-2 text-zinc-200">{c.description || "No description recorded"}</td>
                  <td className="px-4 py-2 text-zinc-400 text-xs">
                    {cadenceLabel(c.cadence)}
                    {!cadenceKnown(c.cadence) && (
                      <div className="text-amber-300 mt-0.5">A billing run cannot read this cadence, so it decides nothing about this charge: it is neither billed nor left off, and every run lists it as unevaluated. Set it to monthly, quarterly or annual.</div>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-zinc-400 text-xs">
                    {c.starts_on || "No start recorded"} → {c.ends_on || <span className="font-sans">Open-ended</span>}
                  </td>
                  <td className="px-4 py-2 font-mono text-zinc-100">
                    {pesoOr(c.amount_minor, <span className="text-amber-300 font-sans text-xs">No amount, so no run can bill it</span>)}
                    {c.amount_minor !== null && c.amount_minor !== undefined && (
                      <span className="text-zinc-600 ml-1.5 text-xs">{c.currency}</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] uppercase font-mono tracking-wider px-1.5 py-0.5 rounded border ${
                      c.active === false
                        ? "bg-zinc-800 text-zinc-400 border-zinc-700"
                        : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"}`}>
                      {c.active === false ? "inactive" : "active"}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <button onClick={() => startEdit(c)} data-testid={`charge-edit-${c.id}`}
                      className="text-zinc-400 hover:text-blue-400 px-1.5"><Pencil size={14} /></button>
                    <button onClick={() => remove(c)} data-testid={`charge-delete-${c.id}`}
                      className="text-zinc-400 hover:text-red-400 px-1.5"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="lg:hidden divide-y divide-zinc-900">
            {rows.map((c) => (
              <div key={c.id} className="p-4 space-y-1.5" data-testid={`charge-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="text-zinc-100 font-medium">{c.description || "No description recorded"}</div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(c)} className="text-zinc-400 hover:text-blue-400"><Pencil size={14} /></button>
                    <button onClick={() => remove(c)} className="text-zinc-400 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="text-xs text-zinc-500">{clientName(c.client_id)} · {contractById[c.contract_id]?.title || c.contract_id}</div>
                <div className="text-xs text-zinc-500">{cadenceLabel(c.cadence)} · {c.starts_on || "no start"} → {c.ends_on || "open-ended"}</div>
                <div className="text-sm font-mono text-zinc-100">
                  {c.amount_minor === null || c.amount_minor === undefined
                    ? "No amount, so no run can bill it"
                    : `${pesoOr(c.amount_minor, "")} ${c.currency}`}
                </div>
                <div className="text-xs text-zinc-500">{c.active === false ? "Inactive, never billed again" : "Active"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
